import { resolveCodDisplayCurrency } from '@modules/CODPayments/utils/resolveCurrencyFromApi';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ColDef } from 'ag-grid-community';
import { Eye } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Payout } from '../models';

const FIELD_TO_API_COLUMN: Record<string, string> = {
  id: 'id',
  client: 'client.name',
  total_amount: 'total_amount',
  items_count: 'items_count',
  payout_date: 'payout_date',
};

const VALID_EXPORT_COLUMNS = new Set([
  'id',
  'client_id',
  'total_amount',
  'payout_date',
  'wallet_transaction_id',
  'items_count',
  'client.name',
]);

// 🎯 Función utilitaria para formatear fechas como dd/mm/yyyy HH:mm
const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return '-';
  }
};

export const useAgGridColumns = () => {
  const { t } = useTranslation('codPayouts');
  const navigate = useNavigate();
  const teamCurrency = useCurrency();

  const columnDefs: ColDef[] = useMemo(() => {
    const columns: CustomColDef[] = [
      {
        field: 'id',
        headerName: t('table.id', 'ID'),
        minWidth: 100,
        sortable: false,
        valueGetter: (params) => params.data?.id ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm font-medium text-foreground">#{params.data?.id ?? '-'}</span>
        ),
      },
      {
        field: 'client',
        headerName: t('table.client', 'Cliente'),
        minWidth: 200,
        sortable: false,
        valueGetter: (params) => params.data?.client?.name ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm text-foreground">{params.data?.client?.name ?? '-'}</span>
        ),
      },
      {
        field: 'total_amount',
        headerName: t('table.total_amount', 'Importe Total'),
        minWidth: 150,
        sortable: true,
        valueGetter: (params) => params.data?.total_amount ?? 0,
        cellRenderer: (params: any) => {
          const amount = params.data?.total_amount ?? 0;
          const payout: Payout | undefined = params.data;
          const currency = resolveCodDisplayCurrency(payout?.currency, teamCurrency);
          const isNegative = amount < 0;
          return (
            <span className={`text-sm font-medium ${isNegative ? 'text-destructive' : 'text-foreground'}`}>
              {formatCurrencyAmount(amount, currency)}
            </span>
          );
        },
      },
      {
        field: 'items_count',
        headerName: t('table.items_count', 'Items'),
        minWidth: 100,
        sortable: false,
        valueGetter: (params) => params.data?.items_count ?? 0,
        cellRenderer: (params: any) => (
          <span className="text-sm text-muted-foreground">{params.data?.items_count ?? 0}</span>
        ),
      },
      {
        field: 'payout_date',
        headerName: t('table.payout_date', 'Fecha de Liquidación'),
        minWidth: 180,
        sortable: true,
        valueGetter: (params) => params.data?.payout_date ?? null,
        cellRenderer: (params: any) => (
          <span className="text-sm text-foreground">{formatDateTime(params.data?.payout_date)}</span>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        minWidth: 80,
        maxWidth: 80,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params: any) => {
          const payout: Payout = params.data;
          if (!payout) return null;

          return (
            <div className="flex items-center justify-center h-full">
              <button
                onClick={() => navigate(`/cod-payments/payouts/${payout.id}`)}
                className="p-2 hover:bg-accent rounded-md transition-colors"
                title={t('actions.view', 'Ver detalle')}
              >
                <Eye className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          );
        },
      },
    ];

    return columns;
  }, [t, navigate, teamCurrency]);

  // 🎯 Función para obtener las columnas en el orden del frontend, filtrando las no válidas
  const getColumnsInOrder = useCallback((): string[] => {
    return columnDefs
      .map((col) => col.field as string | undefined)
      .filter((field): field is string => !!field && field !== 'actions')
      .map((field) => FIELD_TO_API_COLUMN[field] || field)
      .filter((column) => VALID_EXPORT_COLUMNS.has(column)); // Filtrar solo columnas válidas
  }, [columnDefs]);

  return { columnDefs, getColumnsInOrder };
};
